import {Component, ElementRef, OnInit, QueryList, ViewChildren} from '@angular/core';
import {UserService} from "../service/user.service";
import {Subscription} from "rxjs";
import {Router} from "@angular/router";
import {HttpErrorResponse} from "@angular/common/http";
import {CookieService} from "ngx-cookie-service";
import {MAX_ATTEMPTS_COUNT} from "../constants/global.constants";
import {TranslateService} from "@ngx-translate/core";
import {Language} from "../models/language";
import {RegExUtility} from "../utilityClass/RegExUtility";
import {VerifyRequest} from "../interface/verifyRequest";
import {InvitationTokenService} from "../service/invitation-token.service";

@Component({
  selector: 'app-phone-verification',
  templateUrl: './phone-verification.component.html',
  styleUrl: './phone-verification.component.scss'
})
export class PhoneVerificationComponent implements OnInit {

  digits = Array(6).fill(null);
  code = ['', '', '', '', '', ''];
  remainingTime: number = 60;
  private timerInterval: any;
  formattedTime: string = '01:00';
  resendCodeText: string = 'Resend Code';
  private subscriptions: Subscription[] = [];
  userPhoneNumber: string = '';
  countryISO: string = '';
  email: string = '';
  token: string = '';
  invitationToken: string | null;
  message: string = '';
  attemptsCount: number = 0;
  lang = '';
  reservedIdentificationId: string | null = null;
  returnUrl: string | null = null;

  isVerified: boolean = false;
  resendCodeDisabled: boolean = false;
  isVerificationFailed: boolean = false;
  isNavigateToTeamPageAfterLogin: boolean = false;

  @ViewChildren('codeInput') codeInputs!: QueryList<ElementRef<HTMLInputElement>>;

  protected readonly MIDNIGHT = '00:00';

  constructor(
    private userService: UserService,
    private router: Router,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private invitationTokenService: InvitationTokenService
  ) {
    this.userPhoneNumber = this.router.getCurrentNavigation()?.extras.state?.['userPhoneNumber'];
    this.countryISO = this.router.getCurrentNavigation()?.extras.state?.['countryISO'];
    this.email = this.router.getCurrentNavigation()?.extras.state?.['email'];
    this.token = this.router.getCurrentNavigation()?.extras.state?.['token'];
    this.isNavigateToTeamPageAfterLogin = router.getCurrentNavigation()?.extras.state?.['isNavigateToTeamPageAfterLogin'];
    this.invitationToken = this.invitationTokenService.getInvitationToken();
    this.checkIsNeedToNavigateToTeamPageAfterLogin();
    if (this.invitationToken) {
      this.isNavigateToTeamPageAfterLogin = true;
    }
    if (this.userPhoneNumber || this.email) {
      this.startTimer()
    } else {
      this.router.navigate([`error-page`])
    }
  }

  ngOnInit() {
    this.reservedIdentificationId = history.state.reservedIdentificationId || null;
    this.returnUrl = history.state.returnUrl || null;
  }

  get isVerifyButtonDisabled() {
    const isAllFilled = this.code.every(cell => cell !== '');
    return !isAllFilled;
  }

  checkIsNeedToNavigateToTeamPageAfterLogin() {
    if (this.invitationToken) {
      this.isNavigateToTeamPageAfterLogin = true;
    }
  }

  onVerificationCodeChange() {
    this.isVerificationFailed = false;
  }

  validateInput(index: number, el: HTMLInputElement) {
    const inputElement = el;
    if (this.code[index]) {
      inputElement.classList.add('valid');
      inputElement.classList.remove('invalid');
    } else {
      inputElement.classList.remove('valid');
      inputElement.classList.add('invalid');
    }
  }

  onKeyDown(event: KeyboardEvent, index: number, el: HTMLInputElement) {
    const isPasteEvent: boolean = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v';
    if (isPasteEvent) {
      return;
    }

    this.preventDefaultIfNotNumber(event);
    if (event.key === 'Backspace' && !this.code[index] && index > 0) {
      const previousElement = el.previousElementSibling as HTMLInputElement;
      if (previousElement) {
        previousElement.focus();
      }
    }
  }

  onKeyUp(event: KeyboardEvent, index: number, el: HTMLInputElement) {
    this.preventDefaultIfNotNumber(event);
    if (event.key !== 'Backspace' && index < this.digits.length - 1 && this.code[index]) {
      const nextElement = el.nextElementSibling as HTMLInputElement;
      if (nextElement) {
        nextElement.focus();
      }
    }
  }

  startTimer() {
    this.remainingTime = 60;
    this.updateFormattedTime();
    this.resendCodeDisabled = true;
    this.timerInterval = setInterval(() => {
      if (this.remainingTime > 0) {
        this.remainingTime--;
        this.updateFormattedTime();
      } else {
        clearInterval(this.timerInterval);
        this.resendCodeDisabled = false;
        this.resendCodeText = 'Resend Code';
      }
    }, 1000);
  }

  resendCode(event: Event) {
    if (this.userPhoneNumber) {
      this.handlePhoneRequest(event);
    } else if (this.email) {
      this.handelEmailRequest(event);
    }
  }

  private handelEmailRequest(event: Event) {
    event.preventDefault();
    this.startTimer();
    const resendCodeSubscription = this.userService.sendPasswordResetLink(this.email).subscribe()
    this.subscriptions.push(resendCodeSubscription);
  }

  private handlePhoneRequest(event: Event) {
    this.userService.getUserAttemptsCount(this.userPhoneNumber, this.countryISO).subscribe({
      next: (response) => {
        if (response === MAX_ATTEMPTS_COUNT) {
          this.router.navigate([`sign-up`], {state: {isError: true}});
          this.isVerificationFailed = false;
        }
      }
    })
    if (this.attemptsCount === MAX_ATTEMPTS_COUNT) {
      this.router.navigate([`register`], {state: {isError: true}});
      this.isVerificationFailed = false;
    }
    event.preventDefault();
    this.startTimer();
    const resendCodeSubscription = this.userService.resendVerificationCode(this.userPhoneNumber, this.countryISO).subscribe()
    this.subscriptions.push(resendCodeSubscription);
  }

  onVerify() {
    const verificationCode = this.code.join('');
    this.isVerificationFailed = false;

    if (verificationCode.length != 6) {
      this.isVerificationFailed = true;
      this.message = 'verification.enter_code';
      return;
    }
    if (this.userPhoneNumber) {
      this.verifyPhone(verificationCode);
    } else if (this.email) {
      this.verifyEmail(verificationCode);
    }
  }

  private verifyPhone(verificationCode: string) {

    const verifyRequest: VerifyRequest = {
      verificationCode: verificationCode,
      invitationToken: this.invitationToken
    }

    const verifySubscription = this.userService.verifyPhone(verifyRequest).subscribe({
      next: () => {
        this.isVerified = true;
        this.message = 'verification.successful';
        setTimeout(() => {
          this.cookieService.deleteAll();
          this.router.navigate(['login'], {
            state: {
              isNavigateToTeamPageAfterLogin: this.isNavigateToTeamPageAfterLogin,
              reservedIdentificationId: this.reservedIdentificationId,
              returnUrl: this.returnUrl,
              fromRegistration: true
            }, replaceUrl: true
          });
        }, 3000);
      },
      error: (error) => this.handleVerificationError(error)
    });

    this.subscriptions.push(verifySubscription);
  }

  private verifyEmail(verificationCode: string) {
    const verifySubscription = this.userService.verifyEmail(verificationCode).subscribe({
      next: () => {
        this.isVerified = true;
        this.message = 'verification.successful';
        this.router.navigate(['change-password'], {
          state: {
            email: this.email,
            token: this.token,
            reservedIdentificationId: this.reservedIdentificationId,
            returnUrl: this.returnUrl
          }, replaceUrl: true
        })
      },
      error: (error: HttpErrorResponse) => this.handleVerificationError(error)
    });
    this.subscriptions.push(verifySubscription);
  }

  private handleVerificationError(error: HttpErrorResponse) {
    this.isVerificationFailed = true;
    if (error.status === 409) {
      this.message = 'verification.expired_code';
    } else {
      this.message = 'verification.incorrect_code';
    }
  }

  updateFormattedTime() {
    const minutes = Math.floor(this.remainingTime / 60);
    const seconds = this.remainingTime % 60;
    this.formattedTime = `${this.padZero(minutes)}:${this.padZero(seconds)}`;
  }

  padZero(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }

  handlePaste(event: ClipboardEvent, index: number): void {
    event.preventDefault();
    const lastInputElement: HTMLInputElement = this.codeInputs.last.nativeElement;
    const pasteData = event.clipboardData?.getData('text') || '';
    const cleanPasteData = pasteData.replaceAll(' ', '');

    if (!RegExUtility.isIncludesOnlyDigits(cleanPasteData)) {
      return;
    }

    for (let i = 0; i < cleanPasteData.length && index + i < this.digits.length; i++) {
      this.code[index + i] = cleanPasteData[i];
    }
    lastInputElement.focus();
  }

  private preventDefaultIfNotNumber(event: KeyboardEvent) {
    const allowedChars = /[0-9]/;
    const inputChar = event.key;
    if (!allowedChars.test(inputChar) && event.key !== 'Backspace') {
      event.preventDefault();
    }
  }

  protected readonly Language = Language;
}
